import React, {
  useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton, LoadingBounce, TextField, useDialogs,
} from '@michelin/acid-components';
import { DropzoneArea, DropzoneDialog } from 'material-ui-dropzone';
import { getFileUploadURL, getFileDownloadURL } from 'hooks/useFileUpload';
import { InputAdornment, makeStyles } from '@material-ui/core';
import {
  CloudDownload,
  CloudUpload,
  Delete,
  Visibility,
} from '@material-ui/icons';
import DescriptionIcon from '@material-ui/icons/Description';
import ImageIcon from '@material-ui/icons/Image';
import { OverflowMenu } from 'components/OverflowMenu';
import { ProfileMenuItemProps } from '@michelin/central-provider/dist/components/ProfileBox/ProfileMenuItem';
import FileModalPreview from './FileModalPreview';

const useStyles = makeStyles(() => ({
  dropZone: {
    minHeight: 159,
    height: 159,
    marginBottom: 8,
  },
}));

export interface FileData {
  id: string,
  mime: string,
  name: string,
  size: number,
}

async function readFile(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(file);
  });
}

function convertBytes(bytes: number, lang: string) {
  const units = lang.toLocaleLowerCase().split('_').shift() === 'fr'
    ? ['octets', 'Ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo']
    : ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let l = 0;
  let n = bytes;
  while (n >= 1024 && ++l) n /= 1024;
  return (`${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`);
}

interface FileUploadProps {
  onChange?: (data: FileData | null) => void;
  onUploading?: (loading: boolean) => void;
  data?: FileData;
  label?: string;
  required?: boolean;
  readOnly: boolean;
  error?: boolean;
  mode: 'modal' | 'inline' | 'minimal';
  acceptedFiles?: string[];
  downloadEnabled?: boolean;
  previewEnabled?: boolean;
}

function downloadFile(url: string) {
  const a = document.createElement('a');
  a.href = url;
  // For Firefox https://stackoverflow.com/a/32226068
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function useDownload() {
  const dialogs = useDialogs();
  const { t } = useTranslation();

  return async (fileData: FileData, label: string) => {
    try {
      const downloadInfo = await getFileDownloadURL(fileData.id, fileData.name);
      if (!downloadInfo.url) throw new Error('Error getting download authorization');
      downloadFile(downloadInfo.url);
    } catch (e) {
      dialogs.errorDialog(
        [
          t('There was an error while trying to download {{title}}.', { title: label }),
          t('Please try again later...'),
        ],
        t('Downloading error'),
      );
    }
  };
}

function FileUpload({
  onChange, data, onUploading, label, required, readOnly, error, mode, acceptedFiles = ['image/*', 'application/pdf'], downloadEnabled = true, previewEnabled = true,
}: FileUploadProps) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const dialogs = useDialogs();
  const download = useDownload();
  const [uploading, setUploading] = useState<null | boolean>(null);
  const [previewing, setPreviewing] = useState<boolean>(false);

  const [abortController, setAbortController] = useState<null | AbortController>(null);
  useEffect(() => () => {
    if (abortController) abortController.abort();
  }, [abortController]);
  const [fileData, setFileData] = useState<FileData | undefined>(data);
  useEffect(() => setFileData(data), [data]);
  const onUploadingRef = useRef(onUploading);
  useEffect(() => {
    if (!onUploadingRef.current || uploading === null) return;
    onUploadingRef.current(uploading);
  }, [uploading]);

  const save = async (files: File[]) => {
    if (!files[0]) return;
    setOpen(false);
    const mime = files[0].type.replace('x-', '').split(';').shift() || '';
    const validMime = acceptedFiles.some((acceptedMime) => {
      if (mime === acceptedMime) return true;
      if (mime.split('/').shift() !== acceptedMime.split('/').shift()) return false;
      if (acceptedMime.split('/').pop() === '*') return true;
      return false;
    });
    if (!validMime) {
      dialogs.errorDialog(t('Selected file is not valid for this input.'), t('Invalid file type'));
      return;
    }

    setUploading(true);
    const localAbortController = new AbortController();
    setAbortController(localAbortController);
    const currentFile = await readFile(files[0]);
    let uploadInfo;
    try {
      uploadInfo = await getFileUploadURL();
      if (!uploadInfo.url) throw new Error('Error getting upload authorization');
    } catch (e) {
      setUploading(false);
      setAbortController(null);
      dialogs.errorDialog(
        [
          t('There was an error while trying to upload {{title}}.', { title: label }),
          t('Please try again later...'),
        ],
        t('Uploading Error'),
      );
      return;
    }
    try {
      await fetch(uploadInfo.url, {
        method: 'PUT',
        body: currentFile,
        mode: 'cors',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
        signal: localAbortController.signal,
      });
    } catch (e) {
      setUploading(false);
      setAbortController(null);
      if (e.code === 20) return;
      dialogs.errorDialog(
        [
          t('There was an error while trying to upload {{title}}.', { title: label }),
          t('Please try again later...'),
        ],
        t('Uploading Error'),
      );
      return;
    }
    const fd: FileData = {
      id: uploadInfo.fileId,
      name: files[0].name,
      size: files[0].size,
      mime: files[0].type,
    };
    setFileData(fd);
    onChange?.(fd);
    setUploading(false);
    setAbortController(null);
  };

  const uploadFile = () => {
    const i = document.createElement('input');
    i.type = 'file';
    i.accept = acceptedFiles.map((a) => `${a}`).join(', ');
    i.style.display = 'none';
    i.onchange = (event: any) => {
      if (event?.target?.files) save(event.target.files);
      i.remove();
    };
    document.body.appendChild(i);
    i.click();
  };

  const renderActionButtons = () => {
    const overflowMenuItems: ProfileMenuItemProps[] | undefined = [];
    let handleClick;

    if (!uploading && !readOnly) {
      handleClick = () => {
        if (uploading) return;
        if (mode === 'modal') {
          setOpen(true);
          return;
        }
        uploadFile();
      };

      overflowMenuItems.push({
        icon: CloudUpload,
        label: t('Upload File'),
        action: handleClick,
      });
    }

    if (downloadEnabled && fileData) {
      handleClick = () => { download(fileData, label || ''); };

      overflowMenuItems.push({
        icon: CloudDownload,
        label: t('Download'),
        action: handleClick,
      });
    }

    if (fileData && !readOnly) {
      handleClick = () => {
        dialogs.confirmDialog(
          t('Are you sure you want to permanently remove the file?'),
          t('Remove file'),
          t('Yes'),
          t('No'),
          () => {
            setFileData(undefined); onChange?.(null);
          },
        );
      };

      overflowMenuItems.push({
        icon: Delete,
        label: t('Delete'),
        action: handleClick,
      });
    }

    return (
      <InputAdornment position="end">
        {fileData && previewEnabled && (
          <IconButton
            aria-label={t('Preview')}
            onClick={() => { setPreviewing(true); }}
            color="primary"
          >
            <Visibility />
          </IconButton>
        )}
        {(overflowMenuItems.length === 1
          ? (
            <IconButton
              aria-label={overflowMenuItems[0].label}
              onClick={handleClick}
              color="primary"
            >
              {overflowMenuItems[0].icon && React.createElement(overflowMenuItems[0].icon)}
            </IconButton>
          )
          : (
            <OverflowMenu
              style={{ marginTop: 4, marginLeft: 8 }}
              menuItems={overflowMenuItems}
              title={t('Menu')}
              float="right"
            />

          )
        )}
      </InputAdornment>
    );
  };

  return (
    <>
      {(previewing) ? (
        <FileModalPreview
          onClose={() => { setPreviewing(false); }}
          title={t('File preview')}
          fileData={fileData || {} as FileData}
        />
      ) : null}
      {!readOnly && mode === 'inline'
        && (
          <DropzoneArea
            onChange={save}
            filesLimit={1}
            showAlerts={false}
            acceptedFiles={acceptedFiles}
            showPreviews={false}
            showPreviewsInDropzone={false}
            dropzoneText={t('Drag and drop a file here or click')}
            classes={{ root: classes.dropZone }}
          />
        )}
      <TextField
        type="text"
        value={fileData?.name || ''}
        required={required}
        error={error}
        readOnly
        label={label}
        helperText={(fileData?.size && fileData?.mime)
          ? `${convertBytes(fileData.size, i18n.language)} (${fileData.mime.split('/').pop()?.toLocaleUpperCase()})`
          : ''}
        InputProps={{
          readOnly: true,
          startAdornment: fileData && (
            <InputAdornment position="start">
              {fileData.mime.startsWith('image') ? <ImageIcon color={error ? 'error' : 'primary'} /> : <DescriptionIcon color={error ? 'error' : 'primary'} />}
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {uploading ? <LoadingBounce /> : renderActionButtons()}
            </>
          ),
        }}
      />
      <DropzoneDialog
        onSave={save}
        filesLimit={1}
        showFileNames
        open={open}
        showFileNamesInPreview
        useChipsForPreview
        maxFileSize={100 * 1024 * 1024}
        showAlerts={false}
        acceptedFiles={acceptedFiles}
        onClose={() => setOpen(false)}
        cancelButtonText={t('Cancel')}
        dialogTitle={t('Upload {{title}}', { title: label })}
        submitButtonText={t('Submit')}
        previewText={t('Preview')}
        dropzoneText={t('Drag and drop a file here or click')}
      />
    </>
  );
}

export default FileUpload;
